import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styles from './Dashboard.module.css';
import { FaSearch, FaUsers, FaRegFileAlt, FaClipboardList, FaChartBar, FaFolder, FaSignOutAlt, FaUserCircle, FaChartLine, FaCalendarAlt, FaUpload } from 'react-icons/fa';
import logo from '../assets/gmr-logo.jpg';
import { fetchDashboardStats } from '../redux/actions/dashboardActions';
import { RootState } from '../redux/store';
import { AppDispatch } from '../redux/store';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface DashboardProps {
  onLogout: () => void;
  userName?: string;
}

const Dashboard: React.FC<DashboardProps> = ({ onLogout, userName }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { stats, loading, error } = useSelector((state: RootState) => state.dashboard);

  useEffect(() => {
    dispatch(fetchDashboardStats());
  }, [dispatch]);

  // Chart configuration
  const chartData = {
    labels: stats?.monthly_overview.map(item => item.month) || [],
    datasets: [
      {
        label: 'Number of Plans',
        data: stats?.monthly_overview.map(item => item.count) || [],
        backgroundColor: '#4CAF50',
        borderRadius: 6,
        barThickness: 30,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: '#fff',
        titleColor: '#333',
        titleFont: {
          size: 14,
          weight: 'bold' as const
        },
        bodyColor: '#666',
        bodyFont: {
          size: 13
        },
        borderColor: '#e1e1e1',
        borderWidth: 1,
        padding: 12,
        displayColors: false,
        callbacks: {
          label: function(context: any) {
            return `${context.parsed.y} plans`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 12
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#f0f0f0'
        },
        ticks: {
          font: {
            size: 12
          },
          callback: function(value: any) {
            if (value % 1 === 0) {
              return value;
            }
          }
        }
      }
    }
  };

  return (
    <div className={styles.dashboard}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <img src={logo} alt="Global Medical Response Logo" className={styles.logo} />
          <h1>Broker Management Dashboard</h1>
        </div>
        <div className={styles.headerRight}>
          {userName && (
            <span className={styles.userInfo}>
              <FaUserCircle /> {userName}
            </span>
          )}
          <button onClick={onLogout} className={styles.logoutButton}>
            <FaSignOutAlt /> Logout
          </button>
        </div>
      </header>
      <div className={styles.content}>
        <aside className={styles.sidebar}>
          <nav className={styles.nav}>
            <Link to="/plan-search" className={styles.navItem}>
              <FaSearch className={styles.icon} />
              <span>Plan Search</span>
            </Link>
            <Link to="/plan-registration" className={styles.navItem}>
              <FaRegFileAlt className={styles.icon} />
              <span>Plan Registration</span>
            </Link>
            <Link to="/enrollment-dates" className={styles.navItem}>
              <FaCalendarAlt className={styles.icon} />
              <span>Register B2B Enrollments</span>
            </Link>
            <Link to="/census-uploader" className={styles.navItem}>
              <FaUpload className={styles.icon} />
              <span>Census Data Uploader</span>
            </Link>
            <Link to="/reports" className={styles.navItem}>
              <FaChartBar className={styles.icon} />
              <span>Reports</span>
            </Link>
            <Link to="/file-access" className={styles.navItem}>
              <FaFolder className={styles.icon} />
              <span>File Access</span>
            </Link>
          </nav>
        </aside>
        <main className={styles.mainContent}>
          <div className={styles.welcomeMessage}>
            <h2>Welcome to the Broker Management Dashboard</h2>
            <p>Use the sidebar to navigate through different sections of the application.</p>
          </div>
          {loading ? (
            <div className={styles.loading}>Loading dashboard data...</div>
          ) : error ? (
            <div className={styles.error}>{error}</div>
          ) : stats ? (
            <>
              <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                  <FaClipboardList className={styles.statIcon} />
                  <div className={styles.statInfo}>
                    <h3>Total Plans</h3>
                    <p>{stats.total_plans}</p>
                  </div>
                </div>
                <div className={styles.statCard}>
                  <FaUsers className={styles.statIcon} />
                  <div className={styles.statInfo}>
                    <h3>Active Brokers</h3>
                    <p>{stats.active_brokers}</p>
                  </div>
                </div>
                <div className={styles.statCard}>
                  <FaChartLine className={styles.statIcon} />
                  <div className={styles.statInfo}>
                    <h3>Recent Registrations</h3>
                    <p>{stats.recent_registrations}</p>
                  </div>
                </div>
              </div>
              <div className={styles.graphSection}>
                <h3>Monthly Plans Overview</h3>
                <div className={styles.graphContent}>
                  <Bar data={chartData} options={chartOptions} />
                </div>
              </div>
            </>
          ) : null}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;